import { ArrowRight, Boxes, ExternalLink, MessagesSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { useSurveysOnTheWeb } from "@/auth/useMe";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface SectionItem {
  title: string;
  description: string;
  icon: typeof Boxes;
  to: string;
  cta: string;
}

// Only the two surfaces we want people in: the facts they can look up
// (Current Systems) and the survey that turns those facts into positions.
// Opinions, Skills and Admin are admin-only until the interviews have run,
// so they're absent here as well as from the nav.

// Where a member connects the published connector (docs/MCP.md, "For team
// members"). The connector is listed under its registered name, "Northstar".
const CLAUDE_CONNECTORS_URL = "https://claude.ai/settings/connectors";
const SECTIONS: SectionItem[] = [
  {
    title: "Current Systems",
    description:
      "The factual layer — what we run today, what each system is for, which codebases use it, who owns it, and when it was last verified.",
    icon: Boxes,
    to: "/systems",
    cta: "Browse the inventory",
  },
  {
    title: "Surveys",
    description:
      "A short, adaptive interview that captures where you stand on our engineering decisions — auto-saved, resume any time.",
    icon: MessagesSquare,
    to: "/preferences",
    cta: "Open the surveys",
  },
];

function SectionCard({ section }: { section: SectionItem }) {
  const { title, description, icon: Icon, to, cta } = section;
  return (
    <Link
      to={to}
      className="block h-full rounded-card focus:outline-none focus-visible:ring-2 focus-visible:ring-bw-blue focus-visible:ring-offset-2"
    >
      <Card className="flex h-full flex-col transition-colors hover:border-bw-blue">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-4">
          <div className="flex items-start gap-3">
            <Icon className="mt-0.5 h-5 w-5 shrink-0 text-bw-blue" aria-hidden />
            <p className="font-sans text-sm text-bw-ink-muted">{description}</p>
          </div>
          <span className="mt-auto flex items-center gap-1 font-sans text-sm font-semibold text-bw-blue">
            {cta}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </span>
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * Branded landing page, built around one action: run your survey. The hero
 * panel is the primary CTA (the interview is the only thing that produces new
 * knowledge here); the two cards below are the open surfaces, in nav order.
 * With the Surveys page switched off (``SURVEYS_ON_THE_WEB``), the Surveys card
 * goes and the CTA points at the Claude connector instead.
 */
export function HomePage() {
  const surveysOn = useSurveysOnTheWeb();
  const sections = surveysOn ? SECTIONS : SECTIONS.filter((s) => s.to !== "/preferences");
  return (
    <div className="flex flex-col gap-10">
      <section className="flex flex-col gap-3">
        <h1 className="text-4xl font-bold text-bw-navy">North Star</h1>
        <p className="max-w-2xl font-sans text-base text-bw-ink-muted">
          BluWave's record of what we run and what we believe about how we build.
          The facts are already here; the beliefs come from you.
        </p>
      </section>

      {/* Primary CTA. Deliberately loud — the survey is the ask. */}
      {surveysOn && (
        <section className="rounded-card bg-bw-navy px-8 py-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex max-w-2xl flex-col gap-3">
              <h2 className="text-2xl font-bold text-white">Take your survey</h2>
              <p className="font-sans text-base text-white/80">
                A conversation, not a form: pick a topic and North Star interviews
                you on it — your stance, your conviction, your reasoning. A few
                minutes each, auto-saved, resume any time.
              </p>
            </div>
            <Button asChild variant="secondary" size="lg" className="shrink-0">
              <Link to="/preferences">
                Start a survey
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </Button>
          </div>
        </section>
      )}
      {!surveysOn && (
        <section className="rounded-card bg-bw-navy px-8 py-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex max-w-2xl flex-col gap-3">
              <h2 className="text-2xl font-bold text-white">Take your survey in Claude</h2>
              <p className="font-sans text-base text-white/80">
                Surveys run as a conversation with Claude: on your phone, on
                claude.ai, or in Claude Code. Connect once at a laptop: in Claude,
                open Settings, then Connectors, find North Star (listed as
                “Northstar”), click Connect and sign in with your @bluwave.net
                account. Then ask Claude to start your North Star survey.
              </p>
            </div>
            <Button asChild variant="secondary" size="lg" className="shrink-0">
              <a href={CLAUDE_CONNECTORS_URL} target="_blank" rel="noopener noreferrer">
                Open Claude connectors
                <ExternalLink className="h-4 w-4" aria-hidden />
              </a>
            </Button>
          </div>
        </section>
      )}

      <section className="grid gap-6 sm:grid-cols-2">
        {sections.map((section) => (
          <SectionCard key={section.title} section={section} />
        ))}
      </section>
    </div>
  );
}
